import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/lubeConsumptionReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

function prettify(k) {
  return String(k)
    .replace(/^fld_/i, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function buildColumns(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const keys = Object.keys(rows[0] || {});

  // keep max 20 columns to avoid ultra wide printing
  return keys.slice(0, 20).map((k) => ({ key: k, label: prettify(k) }));
}

export default function LubricationConsumptionReport_Print() {
  const navigate = useNavigate();
  const { state, search } = useLocation();

  // ✅ supports both querystring and navigate(state)
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  // ✅ old ASPX sessions mapped to filters
  // ASPX session keys:
  //  branch, month, year, itemcategoryid, itemname, supplier, date
  const filters = useMemo(() => {
    const f = state?.filters || state || {};
    return {
      branchId: toStr(f.branchId || f.branch || qs.get("branchId") || qs.get("branch") || "Select"),
      month: toStr(f.month || qs.get("month") || "Select"), // month number 1..12
      year: toStr(f.year || qs.get("year") || "Select"),
      itemCategoryId: toStr(f.itemCategoryId || f.itemcategoryid || qs.get("itemCategoryId") || qs.get("itemcategoryid") || "37"),
      itemId: toStr(f.itemId || f.itemname || qs.get("itemId") || qs.get("itemname") || "Select"),
      // suppliers can be array in state OR supplier csv in query
      suppliers: Array.isArray(f.suppliers) ? f.suppliers : [],
      supplierCsv: toStr(f.supplier || qs.get("supplier") || qs.get("suppliersCsv") || ""),
      date: toStr(f.date || qs.get("date") || ""),
    };
  }, [state, qs]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");

  const handlePrint = () => window.print();

  const fetchPrint = async () => {
    try {
      setError("");
      setLoading(true);

      if (isSelect(filters.branchId) || isSelect(filters.month) || isSelect(filters.year)) {
        throw new Error("Missing required filters: Branch / Month / Year");
      }

      const params = {
        branchId: filters.branchId,
        month: filters.month,
        year: filters.year,
        itemCategoryId: filters.itemCategoryId,
        itemId: filters.itemId,
        date: filters.date || "",
      };

      // suppliers: prefer array; else csv
      if (filters.suppliers?.length) params.suppliers = filters.suppliers;
      else if (filters.supplierCsv) params.supplier = filters.supplierCsv;

      const res = await axios.get(`${API_BASE}/list`, { params });
      if (!res.data?.success) throw new Error(res.data?.message || "Failed to load");

      setRows(Array.isArray(res.data.rows) ? res.data.rows : []);
      setColumns(Array.isArray(res.data.columns) ? res.data.columns : buildColumns(res.data.rows).map(c => c.key));
      setMeta(res.data.meta || null);
    } catch (e) {
      setError(e.message || "Failed to load print data");
      setRows([]);
      setColumns([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.branchId, filters.month, filters.year, filters.itemId, filters.supplierCsv]);

  // derive header friendly values
  const monthName = useMemo(() => {
    const m = Number(filters.month);
    const arr = [
      "January","February","March","April","May","June",
      "July","August","September","October","November","December",
    ];
    if (m >= 1 && m <= 12) return arr[m - 1];
    return filters.month;
  }, [filters.month]);

  const supplierText = useMemo(() => {
    if (filters.suppliers?.length) return filters.suppliers.join(", ");
    return filters.supplierCsv || "-";
  }, [filters.suppliers, filters.supplierCsv]);

  const colDefs = useMemo(() => {
    if (columns?.length) return columns.map((k) => ({ key: k, label: prettify(k) }));
    return buildColumns(rows);
  }, [columns, rows]);

  return (
    <div className="min-h-screen bg-white p-4">
      {/* Buttons hidden during print */}
      <div className="print:hidden mb-6 text-center flex gap-3 justify-center">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-200 text-black px-6 py-3 text-sm font-medium hover:bg-gray-300"
        >
          Back
        </button>

        <button
          onClick={handlePrint}
          disabled={loading}
          className="bg-black text-white px-6 py-3 text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-60"
        >
          {loading ? "Loading..." : "Print Report"}
        </button>
      </div>

      <div className="max-w-6xl mx-auto border-2 border-black p-4 print:max-w-none print:mx-0 print:border-2">
        <div className="border border-black p-3">
          {/* Header */}
          <div className="border border-black p-3 mb-4 flex items-center gap-5">
            <div className="w-28 h-20 bg-black text-white flex flex-col justify-center items-center text-xs font-bold">
              <div>NISSI</div>
              <div>INFOTECH</div>
              <div className="text-xs">PVT LTD</div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold mb-2">Nissi Infotech</h1>
              <div className="text-xs leading-tight">
                <div>Nissi</div>
                <div className="font-semibold">CIVIL CONSTRUCTION</div>
                <div>3/42-12, Nagappa colony , kavundampalayam ,Tamil Nadu, India .</div>
                <div className="mt-1">
                  Phone No :9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infraconsoft-implementation@nissiinfotech.in
                </div>
              </div>
            </div>
          </div>

          <div className="text-center text-base font-bold my-4">
            Lubrication Consumption Report
          </div>

          {/* Filter box */}
          <div className="border border-black p-2 text-xs mb-4">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="font-semibold">Branch</span> : {filters.branchId}
              </div>
              <div>
                <span className="font-semibold">Month</span> : {monthName}
              </div>
              <div>
                <span className="font-semibold">Year</span> : {filters.year}
              </div>
              <div>
                <span className="font-semibold">Item Category</span> : {filters.itemCategoryId}
              </div>
              <div className="col-span-2">
                <span className="font-semibold">Item</span> : {filters.itemId}
              </div>
              <div className="col-span-2">
                <span className="font-semibold">Supplier(s)</span> : {supplierText}
              </div>
            </div>
          </div>

          {/* Errors / No data */}
          {error ? (
            <div className="text-center text-sm p-6 border border-black">{error}</div>
          ) : !loading && rows.length === 0 ? (
            <div className="text-center text-sm p-6 border border-black">No data found.</div>
          ) : (
            <div className="border border-black overflow-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-black">
                    {colDefs.map((c) => (
                      <th
                        key={c.key}
                        className="border-r border-black p-2 text-left font-semibold whitespace-nowrap"
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={idx} className="border-b border-black">
                      {colDefs.map((c) => (
                        <td key={c.key} className="border-r border-black p-2 whitespace-nowrap">
                          {toStr(r[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-6 p-2 border border-black bg-gray-100 text-xs">
            Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
          </div>

          {/* Screen info */}
          <div className="text-right text-xs mt-2">
            INFRA SCREEN : B-60/1
          </div>
        </div>
      </div>

      {/* print css */}
      <style>{`
        @media print {
          body { margin: 0; padding: 0; }
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden { display: none !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:mx-0 { margin-left: 0 !important; margin-right: 0 !important; }
          .print\\:border-2 { border-width: 2px !important; }
          table { page-break-inside: auto; }
          tr, td, th { page-break-inside: avoid; page-break-after: auto; }
        }
      `}</style>
    </div>
  );
}